import React from "react";
import type { MetricChartData } from "../api/types";

const FALLBACK_COLOR = "#10b981";

// Pull a stronger line color than the pastel `color` field provides.
const COLOR_BY_TITLE = [
  "#10b981",
  "#4db8c8",
  "#9b7fe8",
  "#e8a84d",
  "#f472b6",
  "#0ea5e9",
];

function pickColor(chart: MetricChartData, idx: number): string {
  // The API's `color` is a pastel background — too light for a line.
  // Fall back to a palette indexed by chart order.
  return COLOR_BY_TITLE[idx % COLOR_BY_TITLE.length] ?? FALLBACK_COLOR;
}

/** The series value at a (fractional) frame: linear between the bracketing points, or null when
 * the frame lies outside the series — no reading is invented past the ends. */
export function readingAt(points: { frameIndex: number; value: number }[], frame: number): number | null {
  if (points.length === 0) return null;
  const sorted = [...points].sort((a, b) => a.frameIndex - b.frameIndex);
  if (frame < sorted[0].frameIndex || frame > sorted[sorted.length - 1].frameIndex) return null;
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i];
    if (frame === p.frameIndex) return p.value;
    const q = sorted[i + 1];
    if (q && frame > p.frameIndex && frame < q.frameIndex) {
      const t = (frame - p.frameIndex) / (q.frameIndex - p.frameIndex);
      return p.value + (q.value - p.value) * t;
    }
  }
  return null;
}

export interface ExtraSeries {
  label: string;
  color: string;
  dataPoints: { frameIndex: number; value: number }[];
}

function overlapsAny(placed: Array<{ x: number; y: number; w: number }>, x: number, y: number, w: number): boolean {
  for (const q of placed) if (Math.abs(q.y - y) < 12 && x < q.x + q.w && q.x < x + w) return true;
  return false;
}

/** "Nice" x tick step in seconds so a clip gets at most 6 labels. */
function timeStepFor(spanSec: number): number {
  for (const step of [0.5, 1, 2, 5, 10, 20, 30, 60]) if (spanSec / step <= 5) return step;
  return 120;
}

export function LiveAngleChart({
  chart,
  index = 0,
  phases,
  activePhase,
  cursorFrame,
  primaryLabel,
  color: colorOverride,
  extra,
  extremes = false,
  xTime,
  tall = false,
}: {
  chart: MetricChartData;
  index?: number;
  phases?: { label: string; pct: number }[];
  activePhase?: string;
  /** Live playback: the WHOLE series is drawn and this frame gets a cursor line with the reading
   * there (user, 2026-09-12: "plot the full series and have a line that moves along the plot to
   * show the current reading"). Omitted on the session page's bottom charts. */
  cursorFrame?: number;
  /** Legend label for the chart's own series (e.g. "Left knee") when others share the axes. */
  primaryLabel?: string;
  /** Line colour for the chart's own series; the palette-by-index otherwise. */
  color?: string;
  /** More series on the same axes (e.g. the other side of the body), each with its own colour. */
  extra?: ExtraSeries[];
  /** Mark every series' max and min. */
  extremes?: boolean;
  /** Frame rate: label the x-axis in seconds instead of leaving it bare. */
  xTime?: number;
  /** The big live chart: a taller plot. */
  tall?: boolean;
}) {
  const points = chart.dataPoints ?? [];
  const w = 480;
  const h = tall ? 220 : 160;
  const pad = { l: 32, r: 12, t: 12, b: 22 };

  if (points.length === 0) {
    return (
      <div className="rounded-md bg-slate-50 border border-slate-100 p-4">
        <div className="text-xs font-semibold text-slate-700">{chart.title}</div>
        <div className="text-[0.7rem] text-slate-500 mt-2">No data points returned.</div>
      </div>
    );
  }

  const color = colorOverride ?? pickColor(chart, index);
  const series: ExtraSeries[] = [
    { label: primaryLabel ?? chart.title, color, dataPoints: points },
    ...(extra ?? []).filter((e) => e.dataPoints.length > 0),
  ];
  const allPts = series.flatMap((sr) => sr.dataPoints);
  const xs = allPts.map((p) => p.frameIndex);
  const ys = allPts.map((p) => p.value);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  // Always derive y-range from the data — the API's min/max are configured
  // globals (often 0..100) and squash the line if we honour them.
  const dataMin = Math.min(...ys);
  const dataMax = Math.max(...ys);
  const range = dataMax - dataMin;
  const yPad = (range || Math.max(1, Math.abs(dataMax) * 0.1)) * 0.15;
  const yLow = dataMin - yPad;
  const yHigh = dataMax + yPad;

  const sx = (x: number) =>
    pad.l +
    ((x - xMin) / Math.max(1, xMax - xMin)) * (w - pad.l - pad.r);
  const sy = (y: number) =>
    pad.t + (1 - (y - yLow) / (yHigh - yLow)) * (h - pad.t - pad.b);

  const pathFor = (pts: { frameIndex: number; value: number }[]) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"} ${sx(p.frameIndex).toFixed(2)} ${sy(p.value).toFixed(2)}`).join(" ");

  const yTicks = 3;
  const yStep = (yHigh - yLow) / yTicks;
  const fmt = (v: number) => (Math.abs(yHigh - yLow) < 5 ? v.toFixed(1) : Math.round(v).toString());

  // The cursor: pinned to the plot's edges outside the series, each series labelled only inside it.
  const cursor =
    cursorFrame == null
      ? null
      : (() => {
          const x = sx(Math.min(xMax, Math.max(xMin, cursorFrame)));
          const labelLeft = x > w - pad.r - 70; // flip the label when the cursor nears the right edge
          const readings = series.map((sr) => ({ color: sr.color, value: readingAt(sr.dataPoints, cursorFrame) }));
          return { x, labelLeft, readings };
        })();

  // Time axis: ticks every "nice" number of seconds from the first frame.
  const timeTicks =
    xTime && xTime > 0
      ? (() => {
          const span = (xMax - xMin) / xTime;
          const step = timeStepFor(span);
          const out: Array<{ x: number; label: string }> = [];
          for (let t = 0; t <= span + 1e-9; t += step)
            out.push({ x: sx(xMin + t * xTime), label: `${Number.isInteger(t) ? t : t.toFixed(1)} s` });
          return out;
        })()
      : [];

  // Extremes: one Max and one Min mark per series.
  const extremeMarks = extremes
    ? series.flatMap((sr) => {
        if (sr.dataPoints.length === 0) return [];
        let hi = sr.dataPoints[0];
        let lo = sr.dataPoints[0];
        for (const p of sr.dataPoints) {
          if (p.value > hi.value) hi = p;
          if (p.value < lo.value) lo = p;
        }
        return [
          { kind: "Max", p: hi, color: sr.color },
          { kind: "Min", p: lo, color: sr.color },
        ];
      })
    : [];

  return (
    <div className="rounded-md bg-white border border-slate-100 p-3">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-xs font-semibold text-slate-800">{chart.title}</div>
        <div className="text-[0.65rem] text-slate-500">
          {points.length} pts · {dataMin.toFixed(1)}–{dataMax.toFixed(1)} {chart.unit}
        </div>
      </div>
      {(primaryLabel || (extra && extra.length > 0)) && (
        <div className="flex items-center gap-3 mb-1 text-[0.65rem] text-slate-600" data-testid="chart-legend">
          {series.map((sr) => (
            <span key={sr.label} className="inline-flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: sr.color }} />
              {sr.label}
            </span>
          ))}
        </div>
      )}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const yv = yLow + i * yStep;
          const y = sy(yv);
          return (
            <g key={i}>
              <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#eef2ef" />
              <text
                x={pad.l - 4}
                y={y + 3}
                fontSize="9"
                textAnchor="end"
                fill="#94a3b8"
                data-testid="chart-ytick"
              >
                {Math.abs(yHigh - yLow) < 5 ? yv.toFixed(1) : Math.round(yv).toString()}
              </text>
            </g>
          );
        })}

        {/* Phase markers on the x-axis (P1 → P10). Spaced by `pct` so they align
            visually even though the API doesn't yet expose per-position frameIndex. */}
        {phases && phases.length > 0 && (
          <g>
            {phases.map((p) => {
              const x =
                pad.l + (p.pct / 100) * (w - pad.l - pad.r);
              const isActive = activePhase === p.label;
              return (
                <g key={p.label}>
                  <line
                    x1={x}
                    x2={x}
                    y1={pad.t}
                    y2={h - pad.b}
                    stroke={isActive ? "#10b981" : "#e2e8f0"}
                    strokeDasharray={isActive ? "0" : "3 3"}
                    strokeWidth={isActive ? 1.5 : 1}
                  />
                  <text
                    x={x}
                    y={h - pad.b + 12}
                    fontSize="9"
                    textAnchor="middle"
                    fill={isActive ? "#047857" : "#94a3b8"}
                    fontWeight={isActive ? 600 : 400}
                  >
                    {p.label}
                  </text>
                </g>
              );
            })}
          </g>
        )}

        {timeTicks.map((tk) => (
          <text
            key={tk.label}
            x={tk.x}
            y={h - pad.b + 12}
            fontSize="9"
            textAnchor="middle"
            fill="#94a3b8"
            data-testid="chart-xtick"
          >
            {tk.label}
          </text>
        ))}

        {series.map((sr) => (
          <path
            key={sr.label}
            d={pathFor(sr.dataPoints)}
            stroke={sr.color}
            strokeWidth="1.7"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            data-testid="chart-series"
          />
        ))}

        {(() => {
          // Pills are laid out in order; one that would overlap an earlier pill drops a row.
          const placed: Array<{ x: number; y: number; w: number }> = [];
          return extremeMarks.map((m) => {
            const x = sx(m.p.frameIndex);
            const y = sy(m.p.value);
            const label = `${m.kind} ${fmt(m.p.value)} ${chart.unit}`;
            const boxW = label.length * 5.4 + 10;
            const left = x + boxW + 8 > w - pad.r; // keep the pill inside the plot
            const bx = left ? x - 8 - boxW : x + 8;
            let by = m.kind === "Max" ? Math.max(pad.t, y - 16) : Math.min(h - pad.b - 12, y + 4);
            for (let guard = 0; guard < 6 && overlapsAny(placed, bx, by, boxW); guard++) by += 14;
            placed.push({ x: bx, y: by, w: boxW });
            return (
              <g key={`${m.kind}-${m.color}`}>
                <circle cx={x} cy={y} r="2.6" fill={m.color} />
                <rect x={bx} y={by} width={boxW} height={12} rx={3} fill="#1e293b" opacity={0.85} />
                <text x={bx + boxW / 2} y={by + 9} fontSize="8.5" textAnchor="middle" fill="#ffffff" data-testid="chart-extreme">
                  {label}
                </text>
              </g>
            );
          });
        })()}

        {cursor && (
          <g>
            <line
              x1={cursor.x}
              x2={cursor.x}
              y1={pad.t}
              y2={h - pad.b}
              stroke="#0f172a"
              strokeWidth="1.2"
              strokeDasharray="4 3"
              opacity={0.7}
              data-testid="chart-cursor"
            />
            {cursor.readings.map(
              (r, i) =>
                r.value != null && (
                  <g key={i} data-testid="chart-cursor-reading">
                    <circle cx={cursor.x} cy={sy(r.value)} r="3.5" fill={r.color} stroke="#ffffff" strokeWidth="1.5" />
                    <text
                      x={cursor.labelLeft ? cursor.x - 7 : cursor.x + 7}
                      y={Math.max(pad.t + 9, sy(r.value) - 7)}
                      fontSize="10"
                      fontWeight={600}
                      textAnchor={cursor.labelLeft ? "end" : "start"}
                      fill="#0f172a"
                    >
                      {fmt(r.value)} {chart.unit}
                    </text>
                  </g>
                )
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
